import { ChartTooltipWrapperProps } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipWrapper';
import { TooltipPosition } from '@modules/charts/types';
import { EmptySummaryContent } from '@modules/chatWithFeedback/components/EmptySummaryContent';
import {
  isLabelTypeReason,
  mapEdgeCategoryToCategory,
} from '@modules/common/utils';
import { SearchSummaryHeader } from '@modules/feed/components/SearchSummaryHeader';
import { FeedbackListSummary } from '@modules/oracle/components/FeedbackListSummary';
import { FeedbackListSummaryProps } from '@modules/oracle/components/FeedbackListSummary/FeedbackListSummary';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { Box, Paper, Stack } from '@mui/material';
import { Label } from 'generated/graphql';
import { useCallback, useMemo, useState } from 'react';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { SearchSummaryCardBackground } from '../SearchSummaryCardBackground';
import { SearchSummaryPredictionTooltip } from '../SearchSummaryPredictionTooltip';

interface SearchSummaryContentProps {
  hasNoSummary?: boolean;
  queryContainer: QueryContainer;
  responseID: string | undefined;
}

export const SearchSummaryContent = ({
  hasNoSummary = false,
  responseID,
  queryContainer,
}: SearchSummaryContentProps) => {
  const [tooltipData, setTooltipData] = useState<
    ChartTooltipWrapperProps['payload'] | null
  >(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] =
    useState<TooltipPosition | null>(null);

  const isCompleted = useFeedbackSummaryStore(
    ({ visual }) => visual.isCompleted,
  );

  const handleMouseEnterOnLegend = useCallback(
    (
      event: React.MouseEvent,
      prediction: Label | undefined,
      feedbackCount: number | null,
    ) => {
      if (!prediction) {
        return;
      }

      setShowTooltip(true);
      setTooltipData({
        id: prediction.id,
        label: prediction.displayName,
        value: feedbackCount,
        category: isLabelTypeReason(prediction)
          ? mapEdgeCategoryToCategory(prediction.metadata?.category)
          : undefined,
      });
      setTooltipPosition({
        clientX: event.clientX,
        clientY: event.clientY,
      });
    },
    [],
  );

  const handleMouseLeaveOnLegend = useCallback(() => {
    setShowTooltip(false);
  }, []);

  const predictionProps =
    useMemo((): FeedbackListSummaryProps['predictionProps'] => {
      return {
        textProps: {
          fontWeight: 'bold',
        },
        onMouseEnter: handleMouseEnterOnLegend,
        onMouseLeave: handleMouseLeaveOnLegend,
      };
    }, [handleMouseEnterOnLegend, handleMouseLeaveOnLegend]);

  return (
    <Paper
      variant="outlined"
      sx={{
        border: (theme) =>
          `1px solid ${theme.palette.misc?.wisdom.summary.borderColor}`,
      }}
    >
      <Stack width="100%" position="relative">
        <SearchSummaryCardBackground
          shouldRenderBackground={isCompleted || hasNoSummary}
        />
        <Stack
          py={2}
          px={12}
          width="100%"
          alignItems="center"
          justifyContent="center"
        >
          {hasNoSummary ? (
            <Box width="100%">
              <EmptySummaryContent />
            </Box>
          ) : (
            <Stack
              spacing={2}
              width="100%"
              alignItems="center"
              justifyContent="center"
            >
              <Box width="100%">
                <SearchSummaryHeader
                  queryContainer={queryContainer}
                  hasNoSummary={hasNoSummary}
                  responseID={responseID}
                />
              </Box>
              <Box width="100%">
                <FeedbackListSummary predictionProps={predictionProps} />
              </Box>
            </Stack>
          )}
        </Stack>
      </Stack>
      <SearchSummaryPredictionTooltip
        tooltipData={tooltipData}
        tooltipPosition={tooltipPosition}
        setTooltipPosition={setTooltipPosition}
        showTooltip={showTooltip}
        setShowTooltip={setShowTooltip}
      />
    </Paper>
  );
};
